/**
 * Foule de skieurs : trois poses (ski, assis, marche) x trois LOD, en
 * instanciation GPU. La position de chaque skieur est recalculée à chaque
 * image à partir de son seul mouvement (prédiction locale) : rien n'est
 * envoyé en continu par le serveur.
 */
import * as THREE from 'three/webgpu';
import {
  agentJacket,
  agentSkill,
  counterSlot,
  doorOf,
  queueSlot,
  rideDistance,
  sampleSki,
  seatOffset,
  type AgentWire,
  type Heightfield,
} from '@snowchain/shared';
import { InstancedLodSet, composeYawRoll, linear, type Frustum } from './instancing';
import type { AssetLibrary } from './assets';
import { LOD_DISTANCES } from './lod';
import type { ClientStore } from '../state/store';
import type { Terrain } from './terrain';

/** Couleurs de veste (index `agentJacket`). */
const JACKETS = [0xd96e30, 0x4e8ca8, 0xb8432f, 0x4e7a5a, 0xa97e4f, 0x1b3d4e, 0xe89b62, 0x6c4f8c, 0xe8c547, 0x2f2f38, 0xc94f7c, 0x3fa7a0].map(linear);

export class SkiersLayer {
  readonly group = new THREE.Group();
  private ski: InstancedLodSet;
  private sit: InstancedLodSet;
  private walk: InstancedLodSet;
  private out = new Float64Array(5);
  private mat = new Float32Array(16);
  drawn = 0;
  hidden = 0;

  constructor(
    lib: AssetLibrary,
    private readonly hf: Heightfield,
  ) {
    this.group.name = 'skiers';
    const d = LOD_DISTANCES.skier as number[];
    this.ski = new InstancedLodSet('skier_ski', this.group, lib.get('skier_ski').geometries, lib.instanced, 3000, d, { shadowLods: 1, dynamic: true });
    this.sit = new InstancedLodSet('skier_sit', this.group, lib.get('skier_sit').geometries, lib.instanced, 1500, d, { shadowLods: 1, dynamic: true });
    this.walk = new InstancedLodSet('skier_walk', this.group, lib.get('skier_walk').geometries, lib.instanced, 3000, d, { shadowLods: 1, dynamic: true });
  }

  update(store: ClientStore, t: number, camera: THREE.Camera, frustum: Frustum, terrain: Terrain): void {
    const replica = store.replica;
    this.ski.begin();
    this.sit.begin();
    this.walk.begin();
    let drawn = 0;
    let hidden = 0;
    if (replica) {
      store.refreshQueues();
      const cp = camera.position;
      const out = this.out;
      for (const a of replica.agents.values()) {
        const pose = this.place(store, a, t, out);
        if (pose < 0) {
          hidden++;
          continue;
        }
        const x = out[0] as number;
        const y = out[1] as number;
        const z = out[2] as number;
        // Culling : tuile cachée par le relief, puis frustum.
        const chunk = terrain.chunkAt(x, z);
        if (chunk && !chunk.visible) continue;
        if (!frustum.sphere(x, y + 0.9, z, 1.3)) continue;
        const d = Math.hypot(x - cp.x, y - cp.y, z - cp.z);
        const set = pose === 0 ? this.ski : pose === 1 ? this.sit : this.walk;
        composeYawRoll(this.mat, 0, x, y, z, out[3] as number, pose === 0 ? (out[4] as number) : 0, 1, 1, 1);
        const c = JACKETS[agentJacket(a.c) % JACKETS.length] as [number, number, number];
        if (set.add(this.mat, 0, d, c[0], c[1], c[2])) drawn++;
      }
    }
    this.ski.end();
    this.sit.end();
    this.walk.end();
    this.drawn = drawn;
    this.hidden = hidden;
  }

  /**
   * Calcule la position du skieur à l'instant t. Écrit [x, y, z, cap, roulis]
   * et renvoie la pose (0 ski, 1 assis, 2 marche) ou -1 s'il est invisible.
   */
  private place(store: ClientStore, a: AgentWire, t: number, out: Float64Array): number {
    const m = a.m;
    switch (m[0]) {
      case 0: {
        const [, t0, dur, ax, az, bx, bz] = m;
        const u = dur > 0 ? Math.min(1, Math.max(0, (t - t0) / dur)) : 1;
        const x = ax + (bx - ax) * u;
        const z = az + (bz - az) * u;
        out[0] = x;
        out[1] = this.hf.height(x, z);
        out[2] = z;
        out[3] = Math.atan2(bx - ax, bz - az);
        out[4] = 0;
        return 2;
      }
      case 1: {
        const geo = store.liftGeometry(m[2]);
        if (!geo) return -1;
        const rank = store.queueRank.get(a.i) ?? 0;
        const p = queueSlot(geo, rank);
        out[0] = p[0];
        out[1] = this.hf.height(p[0], p[1]);
        out[2] = p[1];
        out[3] = geo.heading;
        out[4] = 0;
        return 2;
      }
      case 2: {
        const lift = store.replica?.lifts.get(m[1]);
        const geo = lift ? store.liftGeometry(lift.id) : null;
        if (!lift || !geo) return -1;
        if (lift.kind === 'telecabine') return -1;
        const d = rideDistance(geo, lift.cable, m[2], t);
        geo.up.sample(d, out);
        const yaw = Math.atan2(out[3] as number, out[4] as number);
        if (lift.kind === 'teleski') {
          // Tiré au sol par la perche.
          const x = out[0] as number;
          const z = out[2] as number;
          out[1] = this.hf.height(x, z);
          out[3] = yaw;
          out[4] = 0;
          return 0;
        }
        const off = seatOffset(geo, m[3]);
        out[0] = (out[0] as number) + Math.cos(yaw) * off;
        out[2] = (out[2] as number) - Math.sin(yaw) * off;
        out[1] = (out[1] as number) - 2.6;
        out[3] = yaw;
        out[4] = 0;
        return 1;
      }
      case 3: {
        const track = store.pisteTrack(m[1]);
        if (!track) return -1;
        sampleSki(this.hf, track, m[2], m[3], m[4], m[5], agentSkill(a.c), t, out, m[6] ?? 0, m[7] ?? track.length);
        return 0;
      }
      case 4:
        return -1;
      case 5: {
        out[0] = m[2];
        out[1] = this.hf.height(m[2], m[3]);
        out[2] = m[3];
        out[3] = (a.i * 2.399) % (Math.PI * 2);
        out[4] = 0;
        return 2;
      }
      case 6: {
        // File devant un comptoir (caisses, location) : serpentin face à la porte.
        const b = store.replica?.buildings.get(m[2]);
        if (!b) return -1;
        const p = counterSlot(b, store.queueRank.get(a.i) ?? 0);
        const door = doorOf(b);
        out[0] = p[0];
        out[1] = this.hf.height(p[0], p[1]);
        out[2] = p[1];
        out[3] = Math.atan2(door[0] - p[0], door[1] - p[1]);
        out[4] = 0;
        return 2;
      }
      default:
        return -1;
    }
  }

  stats(): { instances: number; triangles: number } {
    return {
      instances: this.ski.instanceCount + this.sit.instanceCount + this.walk.instanceCount,
      triangles: this.ski.triangleCount + this.sit.triangleCount + this.walk.triangleCount,
    };
  }
}
